import type { Location } from "../../state/api.types";
import {
  Badge,
  cx,
  Tooltip,
  TooltipTrigger,
} from "@light-technology/components";
import { FALLBACK_PLAN_DESCRIPTION } from "./accountPlanHelpers";

type PlanTypeInfo = {
  badge: string;
  description: string;
};

const PLAN_TYPES: Readonly<Partial<Record<string, PlanTypeInfo>>> = {
  FALLBACK: {
    badge: "Fallback",
    description:
      "Customer's original contract term has expired and they are now on a month-to-month plan.",
  },
};

type AccountPlanPreviewProps = {
  location: Location;
  showDescription: boolean;
  /** Clip a long plan name to one line, so rows keep a uniform height. */
  truncateName?: boolean;
};

function PlanTypeBadge({
  planType,
  withTooltip,
}: {
  planType: string;
  withTooltip: boolean;
}) {
  const badge = PLAN_TYPES[planType]?.badge;
  if (!badge) return null;

  const pill = (
    <Badge color="gray" size="sm">
      {badge}
    </Badge>
  );

  return (
    <span className="ml-2 shrink-0">
      {withTooltip ? (
        <Tooltip title={FALLBACK_PLAN_DESCRIPTION} placement="top" arrow>
          <TooltipTrigger>{pill}</TooltipTrigger>
        </Tooltip>
      ) : (
        pill
      )}
    </span>
  );
}

function PlanTypeDescription({ planType }: { planType: string }) {
  const description = PLAN_TYPES[planType]?.description;
  if (!description) return null;

  return <p className="whitespace-normal text-tertiary">{description}</p>;
}

export function AccountPlanPreview({
  location,
  showDescription,
  truncateName = false,
}: AccountPlanPreviewProps) {
  return (
    <div className="mb-1">
      <span className="flex items-center">
        {/* The plan they enrolled on; the badge already says "Fallback". */}
        <p className={cx(truncateName && "min-w-0 truncate")}>
          {location.fallback_parent_name ?? location.plan_name}
        </p>
        {location.plan_type && (
          <PlanTypeBadge
            planType={location.plan_type}
            withTooltip={!showDescription}
          />
        )}
      </span>
      {showDescription && location.plan_type && (
        <PlanTypeDescription planType={location.plan_type} />
      )}
    </div>
  );
}
